import { Add } from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/material";
import produce from "immer";
import { useReportBuilderContext } from "Providers/ReportBuilderProvider";
import { useMemo } from "react";
import ChartBuilderMetric from "./ChartBuilderMetric";
import { AccordianChartBuilderSidebarBlock } from "./ChartBuilderSidebarBlock";
import { ApiMetric } from "@incendium/api";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableDndRow } from "./SortableDndRow";

export interface IGroupedMetrics {
  metrics: string[];
  title: string;
}

interface IChartBuilderYAxisProps {
  y: string;
  title?: string;
  subTitle?: string;
  noDrag?: boolean;
  noControls?: boolean;
  noRemove?: boolean;
  maxMetrics?: number;
}

function ChartBuilderYAxis({
  y,
  title,
  subTitle,
  noDrag,
  noControls,
  noRemove,
  maxMetrics,
}: IChartBuilderYAxisProps) {
  const { chart, setChart } = useReportBuilderContext();
  const yAxis: any = useMemo(() => {
    const idx = chart.yAxisKeys.findIndex((v: any) => v.key === y);
    if (idx < 0) {
      return null;
    }
    return chart.yAxisKeys[idx];
  }, [chart, y]);

  const reachedMaxMetrics = useMemo(() => {
    if (!maxMetrics) {
      return false;
    }
    return (yAxis?.fields || []).length >= maxMetrics;
  }, [maxMetrics, yAxis]);

  // Each field must have a unique id. We'll use the value if unique, else fallback to index.
  const fields: string[] = yAxis?.fields || [];
  const fieldIds = fields.map((f, i) =>
    typeof f === "string" ? f || `empty-${i}` : String(f),
  );

  return (
    <AccordianChartBuilderSidebarBlock
      defaultClosed={y === "r" && !yAxis}
      title={
        title || `Verticle (Y) ${y === "l" ? "Left" : "Right"} Axis Values`
      }
      subTitle={
        subTitle ||
        "Select a Metric to display on verticle axis, e.g no. of sessions etc"
      }
    >
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={(event) => {
          const { active, over } = event;
          if (!over || active.id === over.id) return;
          const oldIndex = fieldIds.indexOf(active.id as string);
          const newIndex = fieldIds.indexOf(over.id as string);
          if (oldIndex === -1 || newIndex === -1) return;
          setChart((chart) =>
            produce(chart, (draft) => {
              const idx = draft.yAxisKeys.findIndex((v: any) => v.key === y);
              if (idx < 0) return;
              const fields = (draft.yAxisKeys[idx] as any).fields;
              arrayMove(fields, oldIndex, newIndex).forEach((val, i) => {
                fields[i] = val;
              });
            }),
          );
        }}
      >
        <SortableContext
          items={fieldIds}
          strategy={verticalListSortingStrategy}
        >
          <Stack>
            {noDrag ? (
              <Box>
                {fields.map((_: any, i: number) => (
                  <Box
                    key={i}
                    mb={1.5}
                    sx={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <ChartBuilderMetric
                      chart={chart}
                      setChart={setChart}
                      index={i}
                      yKey={y}
                      noControls={noControls}
                      noRemove={noRemove}
                    />
                  </Box>
                ))}
              </Box>
            ) : (
              fields.map((_: any, i: number) => (
                <SortableDndRow key={fieldIds[i]} id={fieldIds[i]}>
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                    }}
                  >
                    <ChartBuilderMetric
                      chart={chart}
                      setChart={setChart}
                      index={i}
                      yKey={y}
                      noControls={noControls}
                      noRemove={noRemove}
                    />
                  </Box>
                </SortableDndRow>
              ))
            )}
          </Stack>
        </SortableContext>
      </DndContext>

      {!noRemove && !reachedMaxMetrics && (
        <Box mt={3}>
          <Button
            color="secondary"
            size="extraSmall"
            endIcon={<Add />}
            onClick={() => {
              setChart((chart) =>
                produce(chart, (draft) => {
                  const idx = draft.yAxisKeys.findIndex(
                    (v: any) => v.key === y,
                  );
                  if (idx >= 0) {
                    (draft.yAxisKeys[idx] as any).fields.push("");
                    return;
                  }
                  draft.yAxisKeys.push({
                    key: y,
                    fields: [ApiMetric.METRIC_NOT_SET],
                  });
                }),
              );
            }}
          >
            Add additional metric
          </Button>
        </Box>
      )}
    </AccordianChartBuilderSidebarBlock>
  );
}

export default ChartBuilderYAxis;
